import type { UiField, UiSchemaJson, UiSection, UiTab } from '../types/uiSchema';
import { studioPluginRegistry } from '../admin/studioPlugins';

export type SchemaFieldRow = { id: string; label?: string; name?: string; type?: string; required?: boolean; allowed_values?: string[] };

export function buildFieldRefsFromFormSchema(formSchema: Record<string, unknown>): string[] {
  const refs: string[] = [];
  const sections = (formSchema?.sections as Array<Record<string, unknown>> | undefined) || [];
  for (const sec of sections) {
    const sid = String(sec?.id || '').trim();
    if (!sid) continue;
    for (const f of Array.isArray(sec?.fields) ? sec.fields : []) {
      const fid = String((f as SchemaFieldRow)?.id || '').trim();
      if (fid) refs.push(`${sid}.${fid}`);
    }
    for (const g of Array.isArray(sec?.groups) ? sec.groups : []) {
      for (const f of Array.isArray((g as { fields?: unknown })?.fields) ? (g as { fields: SchemaFieldRow[] }).fields : []) {
        const fid = String(f?.id || '').trim();
        if (fid) refs.push(`${sid}.${fid}`);
      }
    }
  }
  return refs;
}

function defaultUiFieldForRef(fieldRef: string): UiField {
  return {
    field_ref: fieldRef,
    visibility: 'always',
    width: 'half',
    density: 'comfortable',
    priority: 'medium',
    display_class: 'primary',
    col_spans: { xs: 12, sm: 12, md: 6, lg: 6 },
  };
}

export function ensureUiFieldsForRefs(refs: string[], existing: UiField[] | undefined): UiField[] {
  const byRef = new Map<string, UiField>();
  for (const f of existing || []) {
    const r = String(f.field_ref || '').trim();
    if (r) byRef.set(r, { ...f, field_ref: r });
  }
  const out: UiField[] = [];
  for (const ref of refs) {
    out.push(byRef.get(ref) ? { ...byRef.get(ref)! } : defaultUiFieldForRef(ref));
  }
  return out;
}

export function upsertUiFieldInList(fields: UiField[] | undefined, fieldRef: string, patch: Partial<UiField>): UiField[] {
  const list = [...(fields || [])];
  const i = list.findIndex((x) => String(x.field_ref) === fieldRef);
  if (i >= 0) {
    // Respect lock unless caller explicitly flips it.
    if (list[i].locked && patch.locked !== false) return list;
    list[i] = { ...list[i], ...patch, field_ref: fieldRef };
  } else {
    list.push({ ...defaultUiFieldForRef(fieldRef), ...patch, field_ref: fieldRef });
  }
  return list;
}

/** One tab per form section — matches typical AI output. */
export function syncTabsFromFormSchema(formSchema: Record<string, unknown> | null): UiTab[] {
  const sections = (formSchema?.sections as Array<Record<string, unknown>> | undefined) || [];
  return sections.map((s, i) => {
    const sid = String(s.id || '').trim() || `section_${i}`;
    const title = String((s.title as string) || (s.name as string) || sid);
    const fields = (Array.isArray(s.fields) ? s.fields : []) as SchemaFieldRow[];
    const fieldRefs = fields.map((f) => `${sid}.${f.id}`).filter(Boolean);
    const uiFields: UiField[] = fieldRefs.map((ref) => ({
      field_ref: ref,
      visibility: 'always',
      width: 'half',
      density: 'comfortable',
      priority: 'medium',
      display_class: 'primary',
      col_spans: { xs: 12, sm: 12, md: 6, lg: 6 },
    }));
    return {
      id: `tab_${sid}`,
      title,
      order: i,
      sections: [
        {
          id: sid,
          title,
          layout: 'two_column',
          density: 'comfortable',
          template: 'default',
          field_refs: fieldRefs,
          fields: uiFields,
        },
      ],
    };
  });
}

export function mergeUiSchemaForSave(
  formSchema: Record<string, unknown>,
  prev: UiSchemaJson | null,
  tabs: UiTab[] | undefined,
): UiSchemaJson {
  const refs = buildFieldRefsFromFormSchema(formSchema);
  const fields = ensureUiFieldsForRefs(refs, prev?.fields);
  const next: UiSchemaJson = {
    ...(prev || {}),
    field_refs: refs,
    fields,
  };

  // Runtime rendering prefers `tabs[].sections[].fields` when present.
  // To ensure edits in the Properties pane are reflected after reload,
  // we normalize per-section `fields` from the global `next.fields`.
  if (tabs && tabs.length > 0) {
    const byRef = new Map<string, UiField>();
    for (const f of fields) {
      const r = String(f.field_ref || '').trim();
      if (!r) continue;
      byRef.set(r, f);
    }

    const ensureSectionFields = (sec: UiSection): UiSection => {
      const refsFromFieldRefs = Array.isArray(sec.field_refs) ? sec.field_refs.map((r) => String(r || '').trim()).filter(Boolean) : [];
      const refsFromFields = Array.isArray(sec.fields) ? sec.fields.map((f) => String(f?.field_ref || '').trim()).filter(Boolean) : [];
      const sectionRefs = refsFromFieldRefs.length ? refsFromFieldRefs : refsFromFields;

      const normalizedFields: UiField[] =
        sectionRefs.length > 0
          ? sectionRefs.map((r) => ({ ...(byRef.get(r) || defaultUiFieldForRef(r)), field_ref: r }))
          : [];

      const normalizedGroups =
        Array.isArray(sec.groups) && sec.groups.length > 0
          ? sec.groups.map((g) => {
              const gFields = Array.isArray(g.fields) ? g.fields : [];
              if (!gFields.length) return g;
              const nextGroupFields = gFields
                .map((gf) => {
                  const fr = String(gf?.field_ref || '').trim();
                  if (!fr) return null;
                  return { ...(byRef.get(fr) || gf), field_ref: fr };
                })
                .filter(Boolean) as UiField[];
              return { ...g, fields: nextGroupFields };
            })
          : sec.groups;

      return {
        ...sec,
        field_refs: sectionRefs,
        fields: normalizedFields,
        groups: normalizedGroups,
      };
    };

    const ensureTabs = (items: UiTab[]): UiTab[] =>
      items.map((t, i) => ({
        ...t,
        order: t.order ?? i,
        sections: (t.sections || []).map((sec) => ensureSectionFields(sec)),
        sub_tabs: Array.isArray(t.sub_tabs) && t.sub_tabs.length > 0 ? ensureTabs(t.sub_tabs) : t.sub_tabs,
      }));

    next.tabs = ensureTabs(tabs);
  } else {
    delete next.tabs;
  }
  return studioPluginRegistry.runPostGenerateMutators(formSchema, next);
}

export function patchSectionInTabs(tabs: UiTab[], sectionId: string, patch: Partial<UiSection>): UiTab[] {
  return tabs.map((tab) => ({
    ...tab,
    sections: (tab.sections || []).map((sec) => {
      if (sec.id !== sectionId) return sec;
      if (sec.locked && patch.locked !== false) return sec;
      return { ...sec, ...patch };
    }),
    sub_tabs: tab.sub_tabs?.length ? patchSectionInTabs(tab.sub_tabs, sectionId, patch) : tab.sub_tabs,
  }));
}

export function findUiSectionInTabs(tabs: UiTab[] | undefined, sectionId: string): UiSection | null {
  if (!sectionId || !tabs?.length) return null;
  for (const tab of tabs) {
    for (const sec of tab.sections || []) {
      if (sec.id === sectionId) return sec;
    }
    const nested = findUiSectionInTabs(tab.sub_tabs, sectionId);
    if (nested) return nested;
  }
  return null;
}

export function validateUiSchemaForSave(formSchema: Record<string, unknown>, uiSchema: UiSchemaJson): string[] {
  const errs: string[] = [];
  for (const f of uiSchema.fields || []) {
    const spans = f.col_spans;
    if (!spans) continue;
    for (const bp of ['xs', 'sm', 'md', 'lg'] as const) {
      const v = spans[bp];
      if (v === undefined) continue;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 1 || n > 12) errs.push(`Invalid ${bp} span for ${f.field_ref}`);
    }
  }
  return [...errs, ...studioPluginRegistry.runPreSaveValidators(formSchema, uiSchema)];
}
